<template>
  <div class="bg-background-light dark:bg-background-dark text-[#0d121b] dark:text-white flex flex-col min-h-screen overflow-x-hidden">
    <AppHeader />
    <main class="flex-grow w-full max-w-[1280px] mx-auto px-4 lg:px-10 py-6 lg:py-10">
      <!-- Banner -->
      <div class="w-full mb-6">
        <div class="bg-cover bg-center flex flex-col justify-end overflow-hidden rounded-xl min-h-[200px] lg:min-h-[280px] relative" data-alt="News Center Banner" style='background-image: linear-gradient(0deg, rgba(19, 91, 236, 0.8) 0%, rgba(19, 91, 236, 0.4) 100%), url("https://lh3.googleusercontent.com/aida-public/AB6AXuAvPFp1QSxXXIOvhfGRosIMMRQGzxsUHqyw5Ut-Rf3SK8vkmcxVEv5jojxBXZNm769rkAbDQSZfgKUSMTahfKzORb4hGs6nZJtlvOfLGnzGz55ugy6XMixtjzxIfITcsa_GX8gu2AoZbJz99ZhKrtDgvnH0kCyeDoK2cHhAjEfijMngbt1j1g-rdSXWQ_SosC_y1MEatVEsiv6TUbBNN13H-caofHdnN7z0la4UEiM4YloJ6G-MlrRsgrQXyjRLfUPEWRDojRwCUS4");'>
          <div class="absolute inset-0 bg-black/20"></div>
          <div class="flex flex-col p-6 lg:p-10 relative z-10">
            <h1 class="text-white tracking-tight text-3xl lg:text-4xl font-bold leading-tight mb-2">新闻中心</h1>
            <p class="text-white/90 text-base lg:text-lg font-medium">湖北省建筑劳务协会官方新闻发布平台</p>
          </div>
        </div>
      </div>

      <!-- Categories -->
      <div class="mb-8 border-b border-[#cfd7e7] dark:border-gray-700 overflow-x-auto">
        <div class="flex min-w-max gap-8">
          <a v-for="cat in categories" :key="cat.id" 
             @click.prevent="handleCategoryChange(cat.id)"
             :class="['flex items-center gap-2 border-b-[3px] pb-3 px-1 cursor-pointer transition-all', 
                      activeCategory === cat.id ? 'border-primary text-primary' : 'border-transparent text-[#4c669a] dark:text-gray-400 hover:text-primary']" 
             href="javascript:;">
            <span class="text-sm font-bold">{{ cat.name }}</span>
          </a>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <!-- Main News Column -->
        <div class="lg:col-span-8 flex flex-col gap-6">
          
          <template v-if="activeCategory !== 'statute'">
            <!-- Enhanced High-Fidelity Skeleton -->
            <div v-if="loading" class="flex flex-col gap-6">
              <!-- Headline Skeleton -->
              <div class="flex flex-col items-stretch rounded-xl border border-[#e7ebf3] bg-white overflow-hidden animate-pulse">
                <div class="w-full h-64 sm:h-80 bg-gray-200"></div>
                <div class="p-6 space-y-4">
                  <div class="h-4 w-20 bg-gray-200 rounded"></div>
                  <div class="h-10 w-3/4 bg-gray-200 rounded"></div>
                  <div class="h-4 w-full bg-gray-100 rounded"></div>
                  <div class="h-4 w-5/6 bg-gray-100 rounded"></div>
                </div>
              </div>
              <!-- List Item Skeletons -->
              <div v-for="i in 3" :key="i" class="flex flex-col sm:flex-row items-stretch rounded-lg border border-[#e7ebf3] bg-white overflow-hidden animate-pulse">
                <div class="w-full sm:w-48 h-48 bg-gray-200"></div>
                <div class="p-5 flex-1 space-y-3 flex flex-col justify-center">
                  <div class="h-4 w-24 bg-gray-200 rounded"></div>
                  <div class="h-7 w-3/4 bg-gray-200 rounded"></div>
                  <div class="h-4 w-full bg-gray-100 rounded"></div>
                  <div class="h-3 w-32 bg-gray-100 rounded mt-auto"></div>
                </div>
              </div>
            </div>
            
            <template v-else-if="newsList && newsList.length > 0">
              <!-- Headline News (if any) - 仅在第一页置顶展示 -->
              <div v-if="headlineNews && currentPage === 1" class="@container">
                <div class="flex flex-col items-stretch rounded-xl shadow-sm border border-[#e7ebf3] dark:border-gray-700 bg-white dark:bg-[#1a202c] overflow-hidden hover:shadow-md transition-shadow">
                  <div class="w-full h-64 sm:h-80 bg-center bg-no-repeat bg-cover relative overflow-hidden bg-gray-100 transition-all duration-700" 
                       :style="{ backgroundImage: `url(${headlineNews.thumbnail || defaultImage})` }"
                       :class="imageLoadedMap[headlineNews.id] ? 'blur-0 scale-100' : 'blur-xl scale-110 opacity-50'">
                    <img :src="headlineNews.thumbnail || defaultImage" class="hidden" @load="onImageLoad(headlineNews.id)" />
                  </div>
                  <div class="p-6 flex flex-col gap-3">
                    <div class="flex items-center gap-2">
                      <span class="bg-primary/10 text-primary text-xs font-bold px-2 py-1 rounded">头条新闻</span>
                      <span class="text-gray-400 text-xs font-normal">{{ formatDate(headlineNews.publishTime) }}</span>
                    </div>
                    <a :href="getLink(headlineNews)" :target="headlineNews.linkType === 1 ? '_blank' : '_self'" class="block group">
                      <h3 class="text-3xl font-extrabold text-[#0d121b] dark:text-white leading-tight group-hover:text-primary transition-colors mb-2">
                        {{ headlineNews.title }}
                      </h3>
                      <h4 v-if="headlineNews.subtitle" class="text-lg font-bold text-[#4c669a] dark:text-blue-400 group-hover:text-primary transition-colors leading-snug">
                        {{ headlineNews.subtitle }}
                      </h4>
                    </a>
                    <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed line-clamp-2 mt-2">
                      {{ headlineNews.summary }}
                    </p>
                    <div class="pt-2">
                      <a :href="getLink(headlineNews)" :target="headlineNews.linkType === 1 ? '_blank' : '_self'" class="text-primary text-sm font-bold flex items-center gap-1 hover:underline">
                        阅读全文 <span class="material-symbols-outlined text-base">arrow_forward</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Standard News List -->
              <div v-for="(news, index) in pagedRegularNews" :key="news.id" class="@container">
                <div class="flex flex-col sm:flex-row items-stretch rounded-lg shadow-sm border border-[#e7ebf3] dark:border-gray-700 bg-white dark:bg-[#1a202c] overflow-hidden hover:shadow-md transition-shadow group relative">
                  <!-- Decorative Index -->
                  <div class="absolute -left-2 -bottom-4 text-7xl font-black text-primary/[0.04] dark:text-white/[0.03] italic select-none pointer-events-none group-hover:text-primary/[0.08] transition-colors z-0">
                    {{ ((currentPage - 1) * pageSize + index + 1).toString().padStart(2, '0') }}
                  </div>
                  <div v-if="news.thumbnail" class="w-full sm:w-48 sm:min-w-48 h-48 sm:h-auto bg-center bg-no-repeat bg-cover transition-all duration-700 relative overflow-hidden bg-gray-100 z-10" 
                       :style="{ backgroundImage: `url(${news.thumbnail})` }"
                       :class="imageLoadedMap[news.id] ? 'blur-0 scale-100' : 'blur-xl scale-110 opacity-50'">
                    <img :src="news.thumbnail" class="hidden" @load="onImageLoad(news.id)" />
                  </div>
                  <div class="flex flex-col justify-center p-5 gap-1 w-full relative z-10">
                    <div class="flex justify-between items-start mb-1">
                      <span class="text-xs font-medium text-[#4c669a] dark:text-gray-400">{{ getCategoryName(news.categoryId) }}</span>
                    </div>
                    <a :href="getLink(news)" :target="news.linkType === 1 ? '_blank' : '_self'" class="block">
                      <h3 class="text-xl font-bold text-[#0d121b] dark:text-white leading-tight group-hover:text-primary transition-colors mb-1">
                        {{ news.title }}
                      </h3>
                      <h4 v-if="news.subtitle" class="text-sm font-bold text-[#4c669a] dark:text-blue-400 group-hover:text-primary transition-colors leading-relaxed mt-1">
                        {{ news.subtitle }}
                      </h4>
                    </a>
                    <p class="text-gray-500 dark:text-gray-400 text-sm line-clamp-2 mt-2">
                      {{ news.summary }}
                    </p>
                    <div class="flex items-center gap-4 mt-auto pt-2">
                      <div class="flex items-center gap-1 text-gray-400 text-xs">
                        <span class="material-symbols-outlined text-sm">calendar_today</span>
                        <span>{{ formatDate(news.publishTime) }}</span>
                      </div>
                      <div class="flex items-center gap-1 text-gray-400 text-xs">
                        <span class="material-symbols-outlined text-sm">visibility</span>
                        <span>{{ news.viewCount }} 阅读</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="flex flex-col items-center justify-center py-20 text-gray-400">
              <span class="material-symbols-outlined text-5xl mb-2">inbox</span>
              <p>暂无相关新闻</p>
            </div>

            <!-- Pagination -->
            <div v-if="regularNews && regularNews.length > pageSize" class="flex justify-center mt-6">
              <el-pagination 
                background 
                layout="prev, pager, next" 
                v-model:current-page="currentPage"
                :page-size="pageSize"
                :pager-count="5"
                :total="regularNews ? regularNews.length : 0" 
              />
            </div>
          </template>
        </div>

        <!-- Right Sidebar -->
        <div class="lg:col-span-4 flex flex-col gap-6">
          <!-- Notifications -->
          <div class="bg-white dark:bg-[#1a202c] rounded-lg shadow-sm border border-[#e7ebf3] dark:border-gray-700 p-5">
            <div class="flex items-center justify-between mb-4 border-b border-[#e7ebf3] dark:border-gray-700 pb-2">
              <h4 class="text-base font-bold text-[#0d121b] dark:text-white flex items-center gap-2">
                <span class="material-symbols-outlined text-primary">notifications_active</span> 通知公告
              </h4>
              <a class="text-xs text-[#4c669a] hover:text-primary cursor-pointer" @click.prevent="activeCategory = 'policy'; fetchNews()">更多 &gt;</a>
            </div>
            <ul v-if="notifications && notifications.length > 0 && !loading" class="flex flex-col gap-3">
              <li v-for="item in notifications" :key="item.id" class="flex gap-3 items-start group">
                <span :class="['w-1.5 h-1.5 rounded-full mt-2 shrink-0 group-hover:scale-125 transition-transform', item.isImportant ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600']"></span>
                <div>
                  <a @click.prevent="$router.push(getLink(item))" class="text-sm text-[#0d121b] dark:text-gray-200 hover:text-primary transition-colors line-clamp-1 cursor-pointer">
                    {{ item.title }}
                  </a>
                  <p class="text-xs text-gray-400 mt-0.5">{{ formatDate(item.createTime) }}</p>
                </div>
              </li>
            </ul>
            <!-- Sidebar Skeleton -->
            <div v-else-if="loading" class="flex flex-col gap-4 animate-pulse">
              <div v-for="i in 5" :key="i" class="flex gap-3 items-start">
                <div class="w-1.5 h-1.5 rounded-full mt-2 bg-gray-200 shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 w-full bg-gray-200 rounded"></div>
                  <div class="h-2 w-20 bg-gray-100 rounded"></div>
                </div>
              </div>
            </div>
            <p v-else class="text-xs text-gray-400 text-center py-4">暂无通知</p>
          </div>


          <!-- Downloads -->
          <div class="bg-white dark:bg-[#1a202c] rounded-lg shadow-sm border border-[#e7ebf3] dark:border-gray-700 p-5">
            <div class="flex items-center justify-between mb-4 pb-2 border-b border-[#e7ebf3] dark:border-gray-700">
              <h4 class="text-base font-bold text-[#0d121b] dark:text-white flex items-center gap-2">
                <span class="material-symbols-outlined text-primary">download</span> 资料下载
              </h4>
            </div>
            <ul v-if="downloads && downloads.length > 0" class="flex flex-col gap-3">
              <li v-for="file in downloads" :key="file.id" class="flex items-center justify-between group cursor-pointer p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded transition-colors">
                <div class="flex items-center gap-3">
                  <span class="material-symbols-outlined text-gray-400">
                    {{ file.fileType === 'pdf' ? 'picture_as_pdf' : 'description' }}
                  </span>
                  <span class="text-sm text-[#0d121b] dark:text-gray-200 line-clamp-1">{{ file.fileName }}</span>
                </div>
                <a :href="getDownloadUrl(file)" target="_blank">
                  <span class="material-symbols-outlined text-gray-400 hover:text-primary text-sm">download</span>
                </a>
              </li>
            </ul>
            <p v-else class="text-xs text-gray-400 text-center py-4">暂无资料</p>
          </div>
        </div>
      </div>
    </main>

    <footer class="bg-white dark:bg-[#1a202c] border-t border-[#e7ebf3] dark:border-gray-700 mt-10">
      <div class="max-w-[1280px] mx-auto px-4 lg:px-10 py-10">
        <div class="flex flex-col md:flex-row justify-between gap-10">
          <div class="flex flex-col gap-4 max-w-sm">
            <div class="flex items-center gap-3 text-[#0d121b] dark:text-white">
              <img alt="Logo" class="h-10 w-10 object-contain" src="/logo.png"/>
              <h2 class="text-lg font-bold">湖北省建筑劳务协会</h2>
            </div>
            <p class="text-[#4c669a] dark:text-gray-400 text-sm leading-relaxed">
              弘扬工匠精神，助推行业发展!<br/>
              ——为建筑劳务保驾护航!
            </p>
          </div>
          <div class="flex flex-wrap gap-10 md:gap-20">
            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-bold text-[#0d121b] dark:text-white">关于我们</h3>
              <router-link class="text-sm text-[#4c669a] dark:text-gray-400 hover:text-primary" to="/about">协会简介</router-link>
              <router-link class="text-sm text-[#4c669a] dark:text-gray-400 hover:text-primary" to="/about#structure">组织架构</router-link>
              <a class="text-sm text-[#4c669a] dark:text-gray-400 hover:text-primary" href="#">联系方式</a>
            </div>
            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-bold text-[#0d121b] dark:text-white">常用链接</h3>
              <a class="text-sm text-[#4c669a] dark:text-gray-400 hover:text-primary" href="#">省住建厅</a>
              <a class="text-sm text-[#4c669a] dark:text-gray-400 hover:text-primary" href="#">全总官网</a>
              <a class="text-sm text-[#4c669a] dark:text-gray-400 hover:text-primary" href="#">中国建设报</a>
            </div>
            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-bold text-[#0d121b] dark:text-white">联系我们</h3>
              <div class="flex items-start gap-2 text-sm text-[#4c669a] dark:text-gray-400">
                <span class="material-symbols-outlined text-base mt-0.5">location_on</span>
                <span>武汉市汉阳区龙阳大道123号<br/>欧亚达汉阳国际广场A座1803</span>
              </div>
              <div class="flex items-center gap-2 text-sm text-[#4c669a] dark:text-gray-400">
                <span class="material-symbols-outlined text-base">call</span>
                <span>027-84881278 (电话/传真)</span>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-10 pt-6 border-t border-[#e7ebf3] dark:border-gray-700 flex flex-col md:flex-row justify-between items-center gap-4">
          <p class="text-xs text-[#4c669a] dark:text-gray-500">©2018 湖北省建筑劳务协会保留所有权利。</p>
          <p class="text-xs text-[#4c669a] dark:text-gray-500">鄂ICP备18023234号-1</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import AppHeader from '@/components/AppHeader.vue';
import newsService from '@/api/newsService';
import dayjs from 'dayjs';

const route = useRoute();

const activeCategory = ref('');
const loading = ref(false);
const newsList = ref([]);
const currentPage = ref(1);
const pageSize = ref(6);
const notifications = ref([]);
const downloads = ref([]);
const openChapter = ref(0);
const imageLoadedMap = ref({});

const headlineNews = computed(() => {
  return newsList.value.find(news => news.isHeadline) || null;
});

const regularNews = computed(() => {
  return newsList.value.filter(news => !news.isHeadline);
});

const pagedRegularNews = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return regularNews.value.slice(start, end);
});

const defaultImage = 'https://lh3.googleusercontent.com/aida-public/AB6AXuDjVG4SdBlz-yIvAsQN15oAd2M6KH-19Pzjc0ao8TXPgMB7s7t999_wVRcn_9GUWiLMHY_MJU3wqQKZBJpXAjiUf7c4dV7IuwUCAFVvEUQtMTdvpvAYu64oyLUJLrs4AqO9mTC8vhigj--gCUKPPrCO9WbQ5OE7qKYFNYIq3ehldwzylwS8q_ETU_xRkwicCaeqicS7TNu3jvzWO9jwdmf_CzEpA7rfzi4d-VaHGXJssGqu1BnTLj_ZBXRbHvMsxb_6s0pRAQVv4uU';

const categories = [
  { id: '', name: '全部新闻' },
  { id: 'association', name: '协会动态' },
  { id: 'policy', name: '政策公告' },
  { id: 'industry', name: '行业资讯' },
  { id: 'party', name: '党建工作' }
];

const renderFormattedItem = (item) => {
  if (!item) return '';
  let formatted = item;
  const articleRegex = /^(第[一二三四五六七八九十百打]+条)(\s+)?/;
  if (articleRegex.test(formatted)) {
    formatted = formatted.replace(articleRegex, '<strong class="font-black text-slate-800 dark:text-white">$1</strong>$2');
  }
  return formatted;
};

const fetchNews = async () => {
  loading.value = true;
  imageLoadedMap.value = {}; // 切换分类时重置
  try {
    const res = await newsService.getNewsList(activeCategory.value);
    newsList.value = res.data || res || [];
  } catch (error) {
    console.error('获取新闻失败:', error);
  } finally {
    loading.value = false;
  }
};

const onImageLoad = (id) => {
  imageLoadedMap.value[id] = true;
};

const fetchSidebarData = async () => {
  try {
    const [notifRes, downRes] = await Promise.all([
      newsService.getNotifications(),
      newsService.getDownloads()
    ]);
    
    // 兼容多种数据返回格式
    const notifData = notifRes?.data || notifRes || [];
    notifications.value = Array.isArray(notifData) ? notifData : [];

    const downData = downRes?.data || downRes || [];
    downloads.value = Array.isArray(downData) ? downData : [];
  } catch (error) {
    console.error('获取侧边栏数据失败:', error);
  }
};

const handleCategoryChange = (id) => {
  activeCategory.value = id;
  currentPage.value = 1; // 切换分类重置页码为1
  fetchNews();
};

const getDownloadUrl = (file) => {
  if (!file || !file.fileUrl) return '#';
  return file.fileUrl.replace('http://8.148.4.196/minio', 'https://www.hblaoxie.com/minio');
};

const getCategoryName = (id) => {
  const cat = categories.find(c => c.id === id);
  return cat ? cat.name : '未知分类';
};

const formatDate = (date) => {
  if (!date) return '-';
  return dayjs(date).format('YYYY-MM-DD');
};

const getLink = (item) => {
  // 1. 处理外部链接（明确要求 linkType === 1 才进行跳转）
  if (item.linkType === 1 && item.externalUrl) return item.externalUrl;
  if (item.linkUrl) return item.linkUrl;

  // 2. 识别类型：存在 categoryId 的是新闻，否则视为通知
  const isNews = Object.prototype.hasOwnProperty.call(item, 'categoryId');
  return isNews ? `/news/detail/${item.id}` : `/notification/detail/${item.id}`;
};

onMounted(() => {
  // 识别 URL 参数，如 ?tab=notice
  if (route.query.tab === 'notice') {
    activeCategory.value = 'policy'; // 对应通知公告的分类 ID
  }
  fetchNews();
  fetchSidebarData();
});
</script>

<style scoped>
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
</style>

<style scoped>
/* Scoped styles if any. Tailwind classes are mostly used. */
</style>
